import { object, string, z } from 'zod';

export type AsyncStatus = 'idle' | 'loading' | 'success' | 'error';

export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export type Writeable<T> = { -readonly [P in keyof T]: T[P] };

export type Maybe<T> = T | null | undefined;
export type Nullish<T> = T | null | undefined;
export function isNullish<A>(value: Nullish<A>): value is null | undefined {
  return value === null || value === undefined;
}
export function isDefined<A>(value: Nullish<A>): value is A {
  return !isNullish(value);
}
export type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

/**
 * Generates dot-notation string paths for nested object properties.
 * e.g. NestedKeyOf<{billGroup: {id: string, name: string}}> = 'billGroup' | 'billGroup.id' | 'billGroup.name'
 * Depth is limited to 3 levels to avoid excessive type recursion.
 */
export type NestedKeyOf<T, Depth extends number[] = []> = Depth extends {
  length: 3;
}
  ? never
  : {
      [K in keyof T & (string | number)]: NonNullable<T[K]> extends object
        ? NonNullable<T[K]> extends unknown[]
          ? `${K}`
          : `${K}` | `${K}.${NestedKeyOf<NonNullable<T[K]>, [...Depth, 0]>}`
        : `${K}`;
    }[keyof T & (string | number)];

export interface StrKeyObj {
  [x: string]: any;
}
export interface IOrderedObj<T> {
  orderedIds: string[];
  byId: { [x: string]: T };
}
export interface StrValueObj {
  [x: string]: string;
}
export interface StrOrNumValueObj {
  [x: string]: string | number;
}
export interface AmountFilterType {
  id: number | string;
  gte: number | string;
  lt?: number | string;
  label: string;
}

// properties common to most API responses:
export const BaseResponseSchema = object({
  createdBy: string(),
  createDate: string(),
  lastModifiedBy: string(),
  modifyDate: string(),
  id: string(),
});
export type IBaseResponse = z.infer<typeof BaseResponseSchema>;

export type RecordAuditInfo = IBaseResponse | null;

// for responses that sometimes return records the backend hasn't saved yet:
export const BaseResponseOptionalSchema = object({
  createdBy: string().nullish(),
  createDate: string().nullish(),
  lastModifiedBy: string().nullish(),
  modifyDate: string().nullish(),
  id: string().nullish(),
});

// This is here an not in utils to avoid circular dependency
export const convertEmailsStringToList = (value: Maybe<string>) =>
  (value ?? '')
    .trim()
    .replace(/[,\r\n\s]/g, ',')
    .split(',')
    .filter(Boolean);

// used in common filter component
export enum FilterTypeOperator {
  IN = 'in',
  CONTAINS = 'contains',
  BETWEEN = 'between',
  // gte + lte (legacy - combined greater than or equal AND less than or equal)
  GLTE = 'glte',
  // Individual date comparison operators
  GT = 'gt', // greater than
  GTE = 'gte', // greater than or equal
  LT = 'lt', // less than
  LTE = 'lte', // less than or equal
  EQ = 'eq', // equals
  EQUAL = 'equal',
  NOT_EQUAL = 'not_equal',
  TOGGLE = 'toggle',
}

export type FilterOptionItemType = {
  label: string;
  value?: string;
  id?: string | number;
};

export interface RenderTableFilterOptionProps {
  filter: FilterType;
  filterOption: FilterOptionType;
  handleFilterChange: (
    val: string[] | { [key: string]: string } | string | boolean,
    filterObject: FilterOptionType,
  ) => void;
}

export type FilterOptionType = {
  title: string;
  key: string;
  operator?: FilterTypeOperator;
  items?: FilterOptionItemType[];
  renderOptionContent?: (
    props: RenderTableFilterOptionProps,
  ) => React.ReactElement;
  // Only availalbe for FilterTypeOperator.TOGGLE
  options?: {
    trueValue?: string;
    falseValue?: string;
  };
};

/**
 * Reusable type for filter values that use min/max range
 * Both fields are optional to support different operator requirements:
 * - EQ: only uses min
 * - GT/GTE: only uses min
 * - LT/LTE: only uses max (or min as fallback)
 * - GLTE/BETWEEN: uses both min and max
 */
export type FilterRangeValue = {
  min?: string;
  max?: string;
};

export type FilterTypeIn = {
  key: string;
  operator: FilterTypeOperator.IN;
  value: string[];
};

export type FilterTypeContains = {
  key: string;
  operator: FilterTypeOperator.CONTAINS;
  value: string;
};

export type FilterTypeBetween = {
  key: string;
  operator: FilterTypeOperator.BETWEEN;
  value: string[] | FilterRangeValue; // Supports both legacy array format and new min/max format
};

export type FilterTypeGlte = {
  key: string;
  operator: FilterTypeOperator.GLTE;
  value: FilterRangeValue;
};

export type FilterTypeToggle = {
  key: string;
  operator: FilterTypeOperator.TOGGLE;
  value: boolean | string;
  options?: {
    trueValue?: string;
    falseValue?: string;
  };
};

export type FilterTypeEqual = {
  key: string;
  operator: FilterTypeOperator.EQUAL;
  options?: string;
  value: string;
  label?: string;
};

export type FilterTypeNotEqual = {
  key: string;
  operator: FilterTypeOperator.NOT_EQUAL;
  value: string;
};

export type FilterTypeGt = {
  key: string;
  operator: FilterTypeOperator.GT;
  value: FilterRangeValue;
};

export type FilterTypeGte = {
  key: string;
  operator: FilterTypeOperator.GTE;
  value: FilterRangeValue;
};

export type FilterTypeLt = {
  key: string;
  operator: FilterTypeOperator.LT;
  value: FilterRangeValue;
};

export type FilterTypeLte = {
  key: string;
  operator: FilterTypeOperator.LTE;
  value: FilterRangeValue;
};

export type FilterTypeEq = {
  key: string;
  operator: FilterTypeOperator.EQ;
  value: FilterRangeValue;
};

export type FilterType =
  | FilterTypeIn
  | FilterTypeContains
  | FilterTypeBetween
  | FilterTypeGlte
  | FilterTypeEqual
  | FilterTypeToggle
  | FilterTypeNotEqual
  | FilterTypeGt
  | FilterTypeGte
  | FilterTypeLt
  | FilterTypeLte
  | FilterTypeEq;

// MCustomNumberInput type
export const MCustomNumberTypeRequired = (message: string) => {
  const schema = z
    .union([z.string(), z.number()])
    .refine((val) => {
      return !!val || val === 0;
    }, message)
    .transform((val) => Number(val));

  // Type cast to make the schema compatible with number fields while accepting string | number input
  return schema as unknown as z.ZodNumber;
};

export const MCustomNumberTypeNullish = z
  .union([z.string().nullish(), z.number().nullish()])
  .transform((val) => {
    const newValue = val === '' || val === null ? null : Number(val);
    if (isNaN(newValue as number)) {
      return null;
    }
    return newValue;
  });

// default schema for options used by MCustomSelect
export interface MCustomSelectOption {
  title: string;
  value: string;
}

// FIXME: this should just be strings instead of enum to avoid runtime characteristics
export enum NumberStyleEnum {
  DECIMAL = 'decimal',
  CURRENCY = 'currency',
  PERCENT = 'percent',
  UNIT = 'unit',
}
export interface FormatNumberOptions {
  style?: NumberStyleEnum;
  zeroStr?: string;
  currency?: string;
  currencySign?: 'standard' | 'accounting';
  minimumFractionDigits?: number;
  maximumFractionDigits?: 'max' | number; // JS max is 20
  use3LetterCodeInsteadOfSymbol?: boolean;
  locale?: string;
}

export enum MimeTypeEnum {
  PLAIN_TEXT = 'text/plain;charset=utf-8',
  CSV = 'text/csv;charset=utf-8',
  OCTET_STREAM = 'application/octet-stream',
  ZIP = 'application/zip',
  JSON = 'application/json;charset=utf-8',
  XML = 'text/xml;charset=utf-8',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  PDF = 'application/pdf',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
}

export enum DraggableItem {
  QUOTE_OFFERING = 'QUOTE_OFFERING',
  APPROVER_ROW = 'APPROVER_ROW',
}

export const EmailTextAreaTypeSchema = z
  .string()
  .nullish()
  .refine(
    (value) => {
      if (!value) {
        return true;
      }

      const emails = convertEmailsStringToList(value);
      const isAllEmailValid = emails.every(
        (email: string) => z.email().safeParse(email).success,
      );

      return isAllEmailValid;
    },
    {
      error: 'Contains invalid email address',
    },
  );

/**
 * Transform a string that has a list of email addresses into an array of email addresses
 * The email addresses are separated by commas, new lines, or white spaces and are normalized and validated
 */
export const EmailStringToEmailListSchema = EmailTextAreaTypeSchema.transform(
  (data) => convertEmailsStringToList(data),
);

export type BillGroupTabTypes =
  | 'details'
  | 'billing-schedule'
  | 'subscriptions'
  | 'payments'
  | 'credits'
  | 'quotes'
  | 'contracts'
  | 'invoices';

export type AccountTabTypes =
  | 'overview'
  | 'contacts'
  | 'bill-groups'
  | 'subscriptions'
  | 'payment-methods'
  | 'payments'
  | 'credits'
  | 'quotes'
  | 'contracts'
  | 'invoices';

// This is here to avoid a circular dependency from types -> utils -> types
export const getRequiredMessage = (title: string) => `${title} is required.`; // error msg for empty input

type Join<K, P> = K extends string | number
  ? P extends string | number
    ? `${K}${'' extends P ? '' : '.'}${P}`
    : never
  : never;

type Prev = [
  never,
  0,
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18,
  19,
  20,
  ...0[],
];

export type Paths<T, D extends number = 3> = [D] extends [never]
  ? never
  : T extends object
    ? {
        [K in keyof T]-?: K extends string | number
          ? `${K}` | Join<K, Paths<T[K], Prev[D]>>
          : never;
      }[keyof T]
    : '';

/**
 * T: Type
 * D: Depth limiter
 */
export type Leaves<T, D extends number = 3> = [D] extends [never]
  ? never
  : T extends object
    ? { [K in keyof T]-?: Join<K, Leaves<T[K], Prev[D]>> }[keyof T]
    : '';

export type BooleanKeys<T> = {
  [K in keyof T]: T[K] extends boolean ? K : never;
}[keyof T];

export const booleanTrueSchema = z.boolean().default(true);
export const booleanFalseSchema = z.boolean().default(false);

export interface BillingPlatformApiError<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  errorCode: string;
  details: string;
  context?: T;
  errorKey: string;
}

export interface BillingPlatformApiErrorKnownResponse<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  debugMessage?: string;
  error: BillingPlatformApiError<T>;
  message: string;
  operation: string;
  status: string;
  subErrors: null | Array<unknown>;
  timestamp: string;
  apierrorresponse?: Maybe<BillingPlatformApiErrorKnownResponse<T>>;
}

// Force caller to handle error in case error format does not match this format
export type BillingPlatformApiErrorResponse<
  T extends Record<string, unknown> = Record<string, unknown>,
> = BillingPlatformApiErrorKnownResponse<T> | unknown;

export function isBillingPlatformApiErrorResponse(
  response: unknown,
): response is BillingPlatformApiErrorKnownResponse {
  if (typeof response !== 'object' || !response || !('error' in response)) {
    return false;
  }
  return (
    typeof (response as any).error === 'object' &&
    (typeof (response as any).message === 'string' ||
      typeof (response as any).message === 'undefined')
  );
}
